import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, ArrowRight, GitBranch } from "lucide-react";

type ChainStep = { id: string; skill: string; condition?: string };

/**
 * Chain Builder visual (Plano v17.07 §V.4 P1).
 * Sequência linear de skills com condicionais entre passos.
 */
export function ChainBuilder({
  initial,
  onChange,
}: {
  initial?: ChainStep[];
  onChange?: (steps: ChainStep[]) => void;
}) {
  const [steps, setSteps] = useState<ChainStep[]>(initial ?? []);
  const [newSkill, setNewSkill] = useState("");

  const update = (next: ChainStep[]) => {
    setSteps(next);
    onChange?.(next);
  };
  const add = () => {
    if (!newSkill.trim()) return;
    update([...steps, { id: crypto.randomUUID(), skill: newSkill.trim() }]);
    setNewSkill("");
  };
  const removeStep = (id: string) => update(steps.filter((s) => s.id !== id));
  const setCondition = (id: string, condition: string) => {
    update(steps.map((s) => (s.id === id ? { ...s, condition } : s)));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><GitBranch className="w-5 h-5 text-primary" /> Chain Builder</CardTitle>
        <CardDescription>Encadeie skills em sequência com condicionais simples (output.score &gt; 0.7, etc).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {steps.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">Nenhum passo. Adicione abaixo.</p>
        )}

        <div className="space-y-2">
          {steps.map((s, i) => (
            <div key={s.id}>
              <div className="flex items-center gap-2 rounded-md border p-2 bg-card">
                <Badge variant="outline" className="font-mono">{i + 1}</Badge>
                <Badge>{s.skill}</Badge>
                <Input
                  className="flex-1 text-xs font-mono"
                  placeholder="condição (ex: output.confidence >= 0.7)"
                  value={s.condition ?? ""}
                  onChange={(e) => setCondition(s.id, e.target.value)}
                />
                <Button size="icon" variant="ghost" onClick={() => removeStep(s.id)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
              {i < steps.length - 1 && (
                <div className="flex justify-center py-1">
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <Input value={newSkill} onChange={(e) => setNewSkill(e.target.value)} placeholder="skill_key (ex: faq-responder)" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button onClick={add}><Plus className="w-4 h-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}
